import usePrefersReducedMotion from '@/hooks/usePrefersReducedMotion';
import backgroundUrl from '@/static/assets/car/driving/background.svg';
import spritesUrl from '@/static/assets/car/driving/sprites.svg';
/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-param-reassign */
import { isFirefox, useLottie, useStorageState, useViewportSize } from '@lemonade-hq/cdk';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import { lerp } from '@lemonade-hq/ts-helpers';
import { rgba } from 'polished';
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type InViewHookResponse, useInView } from 'react-intersection-observer';
import { useTheme } from 'styled-components';
import sparksLottieData from '../../../static/assets/car/driving/sparks.json';
import wheelLottieData from '../../../static/assets/car/driving/wheel.json';
import { CarPageContext } from '../CarPageContext';
import type { CarMode } from '../types';
import { type CheerUp, cheerUps } from './cheerups';
import { Driving, type Environment, type RoadGeneration, type Segment } from './driving';
import type { Coin, Colors, NonNullRef } from './types';
import { useCountingInterval } from './useCountingInterval';
import { type UseGameAudio, useGameAudio } from './useGameAudio';
import { isIOS, isIOS15, isIpad } from './utils';

const SCROLL_PAUSE_DELAY_MS = 250;

export const shouldStopAnimation = (overrideMobileAnimationStop: boolean): boolean =>
  isIOS && !isIpad() && !overrideMobileAnimationStop;
const GAME_TIME = 30;
export const CHEER_UP_TIMEOUT = 1500;
const LONG_COUNTDOWN = 10;
const SHORT_COUNTDOWN = 3;

const useDrivingColors = (): Colors => {
  const theme = useTheme();

  return {
    sky: theme.colors.white,
    minimap: {
      background: theme.colors.white,
      road: '#F0F0F0',
      line: '#D0D0D0',
      marker: theme.colors.primary,
      markerBorder: rgba(theme.colors.primary, 0.05),
    },
    road: {
      background: theme.colors.glass,
      rumble: theme.colors.white,
      rumbleShadow: '#E6E6E6',
      grass: '#f4f4f4',
      lane: theme.colors.white,
    },
    coins: {
      point: {
        color: theme.colors.primary,
        border: rgba(theme.colors.primary, 0.05),
      },
      time: {
        color: theme.colors.green,
        border: rgba(theme.colors.green, 0.05),
      },
    },
  };
};

const useGameKeyboardControls = (env: NonNullRef<Partial<Environment>>, isGameRunning: boolean): void => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const onKeydown = useCallback(
    (ev: KeyboardEvent) => {
      if (isGameRunning) ev.preventDefault();
      if (ev.key === 'ArrowLeft' || ev.key === 'Left') {
        env.current.leftKeyIsDown = true;
      } else if (ev.key === 'ArrowRight' || ev.key === 'Right') {
        env.current.rightKeyIsDown = true;
      } else if (ev.key === 'ArrowDown' || ev.key === 'Down') {
        env.current.downKeyIsDown = true;
      } else if (ev.key === 'ArrowUp' || ev.key === 'Up') {
        env.current.upKeyIsDown = true;
      }
    },
    [isGameRunning],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const onKeyup = useCallback((ev: KeyboardEvent) => {
    if (isGameRunning) ev.preventDefault();
    if (ev.key === 'ArrowLeft' || ev.key === 'Left') {
      env.current.leftKeyIsDown = false;
    } else if (ev.key === 'ArrowRight' || ev.key === 'Right') {
      env.current.rightKeyIsDown = false;
    } else if (ev.key === 'ArrowDown' || ev.key === 'Down') {
      env.current.downKeyIsDown = false;
    } else if (ev.key === 'ArrowUp' || ev.key === 'Up') {
      env.current.upKeyIsDown = false;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', onKeydown);
    window.addEventListener('keyup', onKeyup);

    return () => {
      window.removeEventListener('keydown', onKeydown);
      window.removeEventListener('keyup', onKeyup);
    };
  }, [onKeydown, onKeyup]);
};

interface UseDrivingInViewProps {
  gameNumber: number;
  isGameOn: boolean;
  isScreenCountingDown: boolean;
  setIsGameEnded: Dispatch<SetStateAction<boolean>>;
  setIsScreenLow: Dispatch<SetStateAction<boolean>>;
  setIsGameOn: Dispatch<SetStateAction<boolean>>;
  stopCountdown: () => void;
  stopActiveAudios: () => void;
  overrideMobileAnimationStop: boolean;
  minimapEnabled: boolean;
}

const useDrivingInView = (
  driving: Driving | undefined,
  {
    gameNumber,
    isGameOn,
    isScreenCountingDown,
    stopCountdown,
    stopActiveAudios,
    setIsGameEnded,
    setIsScreenLow,
    setIsGameOn,
    overrideMobileAnimationStop,
    minimapEnabled,
  }: UseDrivingInViewProps,
): ((node?: Element | null | undefined) => void) => {
  const { trackEvent } = useAnalyticsContext();
  const [interactionStartDate, setInteractionStartDate] = useState(new Date());
  const { ref, entry } = useInView();
  const prefersReducedMotion = usePrefersReducedMotion();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const stopGame = useCallback(
    (interactionTime: string) => {
      if (!driving) return;

      if (isGameOn || isScreenCountingDown)
        trackEvent('car_page.game_stopped', {
          gameNumber: (gameNumber - 1).toString(),
          interactionTime,
        });

      driving.endGame();
      if (minimapEnabled) driving.toggleMinimap(true);

      stopActiveAudios();
      stopCountdown();

      setIsGameEnded(true);
      setIsScreenLow(false);
      setIsGameOn(false);
    },
    [driving, gameNumber, isGameOn, isScreenCountingDown],
  );

  // Optimization to not render driving experience when not in viewport
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      shouldStopAnimation(overrideMobileAnimationStop) ||
      !driving ||
      !entry ||
      isFirefox() ||
      isIOS15() ||
      isIOS ||
      prefersReducedMotion
    )
      return;

    if (!entry.isIntersecting) {
      const now = new Date();
      driving.pause();
      stopGame(`${(now.getTime() - interactionStartDate.getTime()) / 1000}`);
    } else {
      setInteractionStartDate(new Date());
      driving.resume();
    }
  }, [entry, driving]);

  return ref;
};

const useMobileScrollPause = (driving: Driving | undefined, isMobileViewport: boolean, isGameOn: boolean): boolean => {
  const scrollTimeoutRef = useRef<number | undefined>(undefined);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (!isMobileViewport || !driving) return;

    const handleScroll = (): void => {
      if (isGameOn) return;

      if (!isScrolling) {
        driving.pause();
        setIsScrolling(true);
      }

      if (scrollTimeoutRef.current !== undefined) {
        window.clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = window.setTimeout(() => {
        driving.resume();
        setIsScrolling(false);
      }, SCROLL_PAUSE_DELAY_MS);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current !== undefined) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
      if (isScrolling && driving) {
        driving.resume();
        setIsScrolling(false);
      }
    };
  }, [driving, isMobileViewport, isGameOn, isScrolling]);

  return isScrolling;
};

interface UseDrivingInitProps {
  driving: Driving | undefined;
  setDriving: Dispatch<SetStateAction<Driving | undefined>>;
  renderCb?: ((currSegment: Segment, env: Environment) => void) | undefined;
  onCoinCollected: ((coin: Coin, env: Environment) => void) | undefined;
  randomSeed?: number;
  roadGeneration?: RoadGeneration;
}

const useDrivingInit = (
  canvas: HTMLCanvasElement | null,
  env: NonNullRef<Partial<Environment>>,
  { renderCb, onCoinCollected, driving, setDriving, randomSeed, roadGeneration }: UseDrivingInitProps,
): boolean => {
  const [isReady, setIsReady] = useState(false);

  const colors = useDrivingColors();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!canvas || driving) return;

    const newDriving = new Driving(
      {
        colors,
        canvas,
        images: [backgroundUrl.src, spritesUrl.src],
        onReady: () => setIsReady(true),
        renderCb,
        onCoinCollected,
        randomSeed,
        roadGeneration,
      },
      env,
    );

    newDriving.run();

    setDriving(newDriving);
  }, [canvas, driving]);

  return isReady;
};

interface UseGameControlsReturnValue {
  carRef: RefObject<HTMLDivElement>;
  wheelRef: RefObject<HTMLDivElement | HTMLImageElement>;
  trackRef: RefObject<HTMLImageElement>;
  sparksRef: RefObject<HTMLDivElement>;
  isMuteButtonVisible: boolean;
  isGameOn: boolean;
  isGameEnded: boolean;
  isScreenLow: boolean;
  isScreenCountingDown: boolean;
  isMuted: boolean;
  points: number;
  activeCheerUps: CheerUp[];
  highScore?: number;
  gameTimer: number;
  gameTimerAddition?: number;
  screenCountdown: number;
  screenBlinkType?: Coin['type'];
  speed: number;
  gameNumber: number;
  setIsGameEnded: Dispatch<SetStateAction<boolean>>;
  setIsScreenLow: Dispatch<SetStateAction<boolean>>;
  setIsGameOn: Dispatch<SetStateAction<boolean>>;
  toggleMuted: () => void;
  stopCountdown: () => void;
  stopActiveAudios: () => void;
  play: () => void;
  replay: () => void;
  honk: () => void;
  renderCb?: ((currSegment: Segment, env: Environment) => void) | undefined;
  onCoinCollected: ((coin: Coin, env: Environment) => void) | undefined;
}

const useGameControls = (
  driving: Driving | undefined,
  mode: CarMode = 'default',
  syncWheelYPosition = false,
  overrideMobileAnimationStop = false,
  minimapEnabled = true,
): UseGameControlsReturnValue => {
  const {
    honkAudio,
    diamondAudio,
    timeAudio,
    countdownAudio,
    winAudio,
    lastSecondsAudio,
    playAgainAudio,
    isMuted,
    playAudio,
    toggleMuted,
    stopActiveAudios,
  } = useGameAudio();

  const [gameNumber, setGameNumber] = useState(0);
  const { storedValue: highScore, setValue: setHighScore } = useStorageState<number>('car_lp_game_highscore', 0);
  const { setIsHidingHeaderCTA } = useContext(CarPageContext);
  const { trackEvent } = useAnalyticsContext();
  const [gameEndTimeout, setGameEndTimeout] = useState<number | undefined>();
  const [isMuteButtonVisible, setIsMuteButtonVisible] = useState(false);
  const [gameTimer, setGameTimer] = useState(0);
  const [isGameOn, setIsGameOn] = useState(false);
  const [isGameEnded, setIsGameEnded] = useState(false);
  const [isScreenLow, setIsScreenLow] = useState(false);
  const [points, setPoints] = useState(0);
  const [activeCheerUps, setActiveCheerUps] = useState<CheerUp[]>([]);
  const [screenBlinkType, setScreenBlinkType] = useState<Coin['type'] | undefined>();
  const [gameTimerAddition, setGameTimerAddition] = useState<number | undefined>();
  const [speed, setSpeed] = useState(3000);
  const [screenCountdown, isScreenCountingDown, countdown, stopCountdown] = useCountingInterval({
    interval: 1000,
    from: LONG_COUNTDOWN,
    to: -2,
    delay: true,
  });
  const prefersReducedMotion = usePrefersReducedMotion();

  const carRef = useRef<HTMLImageElement>(null);
  const trackRef = useRef<HTMLImageElement>(null);
  const sparksRef = useRef<HTMLDivElement>(null);
  const sparksLottie = useLottie(sparksRef, {
    animationData: sparksLottieData,
    lottieConfig: {
      autoplay: false,
      loop: false,
    },
  });
  const wheelRef = useRef<HTMLDivElement | HTMLImageElement>(null);
  const wheelLottie = useLottie(mode === 'default' ? wheelRef : { current: null }, {
    animationData: wheelLottieData,
    lottieConfig: {
      autoplay: true,
      loop: true,
    },
  });

  // Lower time in game
  useEffect(() => {
    const interval = setInterval(() => {
      if (isGameOn) {
        setGameTimer(time => (time - 1 < 0 ? 0 : time - 1));
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isGameOn]);

  // Hide CTA in header if in game mode
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setIsHidingHeaderCTA(!isGameOn && !isScreenCountingDown);
  }, [isGameOn, isScreenCountingDown]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const startGame = useCallback(() => {
    if (
      shouldStopAnimation(overrideMobileAnimationStop) ||
      !driving ||
      isFirefox() ||
      isIOS15() ||
      isIOS ||
      prefersReducedMotion
    )
      return;

    driving.startGame();

    setIsGameOn(true);
    setIsGameEnded(false);
    setGameTimer(GAME_TIME);
    setPoints(0);
    setActiveCheerUps([]);
  }, [driving, overrideMobileAnimationStop]);

  // Start game when counting down has finished
  useEffect(() => {
    if (!driving) return;

    if (screenCountdown === 0) {
      startGame();
    }
  }, [screenCountdown, driving, startGame]);

  // Play last seconds audio
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!driving) return;

    if (isGameOn && gameTimer <= 5 && gameTimer !== 0) playAudio(lastSecondsAudio);
  }, [isGameOn, gameTimer, driving, playAudio]);

  // End game
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!driving) return;
    if (!isGameOn || gameTimer > 0) return;

    driving.endGame();

    playAudio(winAudio);

    const currentHighScore = highScore ?? 0;
    const bestScore = currentHighScore < points ? points : currentHighScore;
    setHighScore(bestScore);

    trackEvent('car_page.game_ended', {
      points: points.toString(),
      bestScore: bestScore.toString(),
      gameNumber: gameNumber.toString(),
    });

    const showGameEndedTimeout = setTimeout(() => {
      setIsGameEnded(true);
      setIsScreenLow(false);
    }, 2000);

    const gameEndedTimeout = window.setTimeout(() => {
      setIsGameOn(false);
      if (minimapEnabled) driving.toggleMinimap(true);
    }, 10000);

    setGameEndTimeout(gameEndedTimeout);

    return () => {
      clearTimeout(showGameEndedTimeout);
      clearTimeout(gameEndedTimeout);
    };
  }, [points, isGameOn, gameTimer, driving, playAudio, highScore]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const prepareGame = useCallback(() => {
    if (!driving) return;

    if (wheelLottie != null && (wheelLottie.currentFrame < 20 || wheelLottie.currentFrame > 54)) {
      wheelLottie.goToAndPlay(20);
    }

    setIsMuteButtonVisible(true);
    setIsScreenLow(true);
    if (minimapEnabled) driving.toggleMinimap(false);
    const countdownSeconds = highScore === 0 && gameNumber === 0 ? LONG_COUNTDOWN : SHORT_COUNTDOWN;
    setGameNumber(gameNumber + 1);

    setTimeout(
      () => {
        playAudio(countdownAudio);
      },
      (countdownSeconds - SHORT_COUNTDOWN) * 1000,
    );
    countdown(countdownSeconds);
  }, [driving, gameNumber, countdown, playAudio, highScore]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const play = useCallback(() => {
    if (shouldStopAnimation(overrideMobileAnimationStop) || isFirefox() || isIOS15() || isIOS || prefersReducedMotion)
      return;
    window.scroll({ top: 0, behavior: 'smooth' });
    trackEvent('car_page.game_started', { gameNumber: gameNumber.toString() });
    prepareGame();
  }, [prepareGame, gameNumber, overrideMobileAnimationStop]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const replay = useCallback(() => {
    clearTimeout(gameEndTimeout);

    playAudio(playAgainAudio);
    trackEvent('car_page.game_restarted', {
      gameNumber: gameNumber.toString(),
    });
    prepareGame();
  }, [prepareGame, gameEndTimeout, playAudio, gameNumber]);

  const renderCb = useCallback(
    (currentSegment: Segment, env: Environment) => {
      if (!wheelRef.current) return;
      if (!carRef.current) return;

      const wheelRotation = env.isInGameMode
        ? env.leftKeyIsDown
          ? -6
          : env.rightKeyIsDown
            ? 6
            : currentSegment.curve * 4
        : currentSegment.curve * 4;

      const carTranslation = +((currentSegment?.p1.world.y - currentSegment?.p2.world.y) / 5).toFixed(2);
      const carTransformation = `translateY(${carTranslation}px)`;
      if (carRef.current.style.transform !== carTransformation) carRef.current.style.transform = carTransformation;

      const wheelTranslation = syncWheelYPosition
        ? carTranslation
        : +((currentSegment?.p2.world.y - currentSegment?.p1.world.y) / 15).toFixed(2);
      const wheelTransformation = `rotate(${+wheelRotation.toFixed(2)}deg) translateY(${wheelTranslation}px)`;

      if (wheelRef.current.style.transform !== wheelTransformation)
        wheelRef.current.style.transform = wheelTransformation;

      if (trackRef.current) {
        const curve = currentSegment.curve;
        const trackRotation = curve * 1.5;
        const trackTranslationX = curve * 1.5;
        const trackSkewX = curve * -1.5;
        const trackTransformation = `perspective(500px) rotateX(15deg) rotate(${+trackRotation.toFixed(2)}deg) translateX(${+trackTranslationX.toFixed(2)}px) skewX(${+trackSkewX.toFixed(2)}deg)`;

        if (trackRef.current.style.transform !== trackTransformation) {
          trackRef.current.style.transform = trackTransformation;
        }
      }

      setSpeed(env.speed);
    },
    [syncWheelYPosition],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const onCoinCollected = useCallback(
    (coin: Coin) => {
      if (coin.type === 'point') {
        sparksLottie?.goToAndPlay(0);
        playAudio(diamondAudio);
        let currentPoints = 0;
        setPoints(points => {
          currentPoints = points + (coin.mega ? 5 : 1);

          return currentPoints;
        });

        if (Math.random() > 0.3) {
          const level =
            Object.keys(cheerUps)
              .reverse()
              .find(key => currentPoints >= +key) || '0';
          const levelCheerUps = cheerUps[+level];

          const getCheerUp = (): CheerUp => levelCheerUps[Math.round(lerp(0, levelCheerUps.length - 1, Math.random()))];

          let randomCheerUp = getCheerUp();

          setActiveCheerUps(cheerUps => {
            while (cheerUps.includes(randomCheerUp)) {
              randomCheerUp = getCheerUp();
            }

            return [...cheerUps, randomCheerUp];
          });

          setTimeout(() => {
            setActiveCheerUps(cheerUps => {
              cheerUps.pop();

              return cheerUps;
            });
          }, CHEER_UP_TIMEOUT);
        }
      }
      if (coin.type === 'time') {
        setScreenBlinkType(coin.type);

        playAudio(timeAudio);
        const timeAddition = coin.mega ? 5 : 2;

        setGameTimer(time => time + timeAddition);
        setGameTimerAddition(timeAddition);
        setTimeout(() => {
          setGameTimerAddition(undefined);
        }, 1000);
      }

      setTimeout(() => {
        setScreenBlinkType(undefined);
      }, 150);
    },
    [playAudio, diamondAudio, cheerUps, timeAudio],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const honk = useCallback(() => {
    playAudio(honkAudio);
  }, [honkAudio]);

  return {
    carRef,
    wheelRef,
    trackRef,
    sparksRef,
    isGameOn,
    isGameEnded,
    isScreenLow,
    isScreenCountingDown,
    isMuted,
    isMuteButtonVisible,
    points,
    highScore,
    gameTimer,
    gameTimerAddition,
    screenCountdown,
    screenBlinkType,
    activeCheerUps,
    speed,
    gameNumber,
    setIsGameEnded,
    setIsScreenLow,
    setIsGameOn,
    honk,
    play,
    replay,
    toggleMuted,
    stopActiveAudios,
    stopCountdown,
    renderCb,
    onCoinCollected,
  };
};

export interface UseDriving {
  carRef: RefObject<HTMLDivElement>;
  wheelRef: RefObject<HTMLDivElement | HTMLImageElement>;
  trackRef: RefObject<HTMLImageElement>;
  sparksRef: RefObject<HTMLDivElement>;
  isReady: boolean;
  isGameOn: boolean;
  isGameEnded: boolean;
  isScreenLow: boolean;
  isScreenCountingDown: boolean;
  isMuted: boolean;
  isMuteButtonVisible: boolean;
  isScrollPaused: boolean;
  currentTime: string;
  speed: number;
  points: number;
  highScore?: number;
  gameTimer: number;
  gameTimerAddition?: number;
  screenCountdown: number;
  screenBlinkType?: Coin['type'];
  activeCheerUps: CheerUp[];
  play: () => void;
  replay: () => void;
  toggleMuted: UseGameAudio['toggleMuted'];
  honk: () => void;
}

export interface UseDrivingOptions {
  initialProps?: Partial<Environment>;
  mode?: CarMode;
  syncWheelYPosition?: boolean;
  secondaryScreenView?: Environment['secondaryScreenView'];
  overrideMobileAnimationStop?: boolean;
  randomSeed?: number;
  roadGeneration?: RoadGeneration;
  showMinimap?: boolean;
}

export const useDriving = (
  canvas: HTMLCanvasElement | null,
  options: UseDrivingOptions = {},
): [InViewHookResponse[0], Driving | undefined, UseDriving] => {
  const {
    initialProps = {},
    mode = 'default',
    syncWheelYPosition = false,
    secondaryScreenView,
    overrideMobileAnimationStop = false,
    randomSeed,
    roadGeneration,
    showMinimap,
  } = options;
  const [driving, setDriving] = useState<Driving>();
  const minimapEnabled = showMinimap ?? mode !== 'tesla';
  const env = useRef<Partial<Environment>>({
    ...initialProps,
    isMinimapVisible: minimapEnabled,
    secondaryScreenView,
    overrideMobileAnimationStop,
  });
  const [now, setNow] = useState(new Date());

  const {
    carRef,
    wheelRef,
    trackRef,
    sparksRef,
    isGameOn,
    isGameEnded,
    isScreenLow,
    isScreenCountingDown,
    isMuted,
    isMuteButtonVisible,
    points,
    highScore,
    gameTimer,
    gameTimerAddition,
    screenCountdown,
    screenBlinkType,
    activeCheerUps,
    speed,
    gameNumber,
    setIsGameEnded,
    setIsScreenLow,
    setIsGameOn,
    play,
    replay,
    toggleMuted,
    honk,
    renderCb,
    onCoinCollected,
    stopCountdown,
    stopActiveAudios,
  } = useGameControls(driving, mode, syncWheelYPosition, overrideMobileAnimationStop, minimapEnabled);

  useGameKeyboardControls(env, env.current.isInGameMode || isScreenCountingDown);

  const isReady = useDrivingInit(canvas, env, {
    driving,
    setDriving,
    renderCb,
    onCoinCollected,
    randomSeed,
    roadGeneration,
  });

  const { isMobileViewport } = useViewportSize();
  const isScrollPaused = useMobileScrollPause(driving, isMobileViewport, isGameOn);

  // Sync time
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const currentTime = useMemo(() => {
    let hours = now.getHours() % 12;
    hours = hours !== 0 ? hours : 12;

    return `${hours.toString().padStart(2, '0')}:${now
      .getMinutes()
      .toString()
      .padStart(2, '0')} ${now.getHours() >= 12 ? 'PM' : 'AM'}`;
  }, [now]);

  const normalizedSpeed = useMemo(() => (speed * 2.5) / 100, [speed]);

  const ref = useDrivingInView(driving, {
    gameNumber,
    isGameOn,
    isScreenCountingDown,
    stopCountdown,
    stopActiveAudios,
    setIsGameEnded,
    setIsScreenLow,
    setIsGameOn,
    overrideMobileAnimationStop,
    minimapEnabled,
  });

  return [
    ref,
    driving,
    {
      carRef,
      wheelRef,
      trackRef,
      sparksRef,
      isReady,
      isGameOn,
      isGameEnded,
      isScreenLow,
      isScreenCountingDown,
      isMuted,
      isMuteButtonVisible,
      isScrollPaused,
      currentTime,
      speed: normalizedSpeed,
      points,
      highScore,
      gameTimer,
      gameTimerAddition,
      screenCountdown,
      screenBlinkType,
      activeCheerUps,
      play,
      replay,
      toggleMuted,
      honk,
    },
  ];
};
